/**
 * Settings feature module — device/UI/locale/feature-flag verbs plus the legacy
 * `closePanel` fallback. Case bodies moved verbatim from embeddedBridge.ts.
 */

import type { HandlerMap } from '../dispatch';
import { diagLog } from '../transport';
import { useUiStore } from '@/store/uiStore.ts';
// 20260903 - Its own leaf store, deliberately not a uiStore slice: dialogs all over the app read this
// flag, and a uiStore edge would drag embeddedBridge into each of their module graphs. See that file.
import { useInputCapabilitiesStore } from '@/platform/inputCapabilitiesStore.ts';
// 20260919 - IPHONE DUO SIDE DOCK: its own leaf store too, and for the identical reason — shell
// chrome all over the app reads the safe area, and a uiStore edge would drag embeddedBridge into
// each of their module graphs. See platform/foldStateStore.ts.
import { useFoldStateStore } from '@/platform/foldStateStore.ts';
import {
  readLaptopBand,
  readOcclusionExtent,
  resolveVerticalBarEdge,
  type FoldDivision,
  type FoldState,
  type SafeAreaInsets,
} from '@/platform/foldState.ts';
// 20260920 - The "device is changing shape" signal every layout snaps on. A leaf module too (one
// flag and a timer, no store edge), so it costs this graph nothing. See the mark in setFoldState.
import { markViewportChange } from '@/platform/viewportTransition.ts';
import { useTelemetryStore, isTelemetryEntitlement } from '@/telemetry/telemetryStore';
import { scheduleFlush } from '@/telemetry/trackEvent';

export interface SetUIScalingMessage {
  type: 'setUIScaling';
  payload: { level: number };
}

export interface SetLocaleMessage {
  type: 'setLocale';
  payload: { locale: string };
}

export interface SetFeatureFlagsMessage {
  type: 'setFeatureFlags';
  payload: { scriptCoverage?: boolean; screenplayBlocking?: boolean; sceneShotSettings?: boolean };
}

// 20260903 - TEXT-FIELD AUTO-FOCUS POLICY: the ONE half of the host's input capabilities that can
// change mid-session (a Magic Keyboard attached or detached). Platform and form factor ride the URL
// instead — see platform/inputCapabilities.ts.
//
// 20260923 - AND THE POINTER, which can be hot-plugged just as easily (a trackpad, or a mouse; on
// iPadOS both are a GCMouse). BOTH KEYS ARE OPTIONAL and a push carries only the device that
// changed: TimelineWebViewPanel raises them from two separate native watchers, so a keyboard event
// has nothing to say about the pointer and must not be read as saying it. The handler merges.
export interface SetInputCapabilitiesMessage {
  type: 'setInputCapabilities';
  payload: { hardwareKeyboard?: boolean; pointerDevice?: boolean };
}

// 20260919 - IPHONE DUO SIDE DOCK: the fold geometry the web layer cannot see for itself. WKWebView
// on iOS 27.1 exposes no Viewport Segments and no Device Posture (the implementation is in the
// binary behind disabled preference keys — docs/reference/iphone-duo.md §4.3), so posture, the fold
// rectangle and which display we are on can only arrive from Unity. `source` is NOT on the wire:
// the store stamps it, so a push can never claim to be something it is not.
// 20260926 - `barHeadExtent` / `barFootExtent` are optional on the wire: a host built before them
// sends the pair alone, and that must read as "extents not reported", not as a malformed push.
// 20260929 - `laptopBand` likewise: absent is "not the laptop pose" (every host that predates it, and
// the Purchasing / Sign In / canvas overlay pages, which share the builder and ignore it).
export interface SetFoldStateMessage {
  type: 'setFoldState';
  payload: Omit<FoldState, 'source' | 'barHeadExtent' | 'barFootExtent' | 'laptopBand'>
    & Partial<Pick<FoldState, 'barHeadExtent' | 'barFootExtent' | 'laptopBand'>>;
}

export interface SetDeviceIdMessage {
  type: 'setDeviceId';
  /** `entitlement` is OPTIONAL and stays optional: hosts before 2026-09-21 omit it, and Unity
   *  omits it when the purchasing manager is not up yet (never "free" for "not loaded"). */
  payload: { deviceId: string; entitlement?: string };
}

export interface SetFunnelIdMessage {
  type: 'setFunnelId';
  payload: { funnelId: string };
}

// Give closePanel a real interface so Task 13's exhaustive BridgeHandlers mapped type
// needs no special-casing: it joins SettingsMessage like any other member.
export interface ClosePanelMessage { type: 'closePanel'; payload?: unknown }

export type SettingsMessage =
  | SetUIScalingMessage
  | SetLocaleMessage
  | SetFeatureFlagsMessage
  | SetInputCapabilitiesMessage
  | SetFoldStateMessage
  | SetDeviceIdMessage
  | SetFunnelIdMessage
  | ClosePanelMessage;

// 20260919 - IPHONE DUO SIDE DOCK: the payload guard for setFoldState, inline for the same reason
// setFeatureFlags' and setInputCapabilities' guards are — a malformed push must leave the measured
// `env()` answer standing rather than resolve to a confident wrong geometry. This one has to be
// stricter than its siblings because it does not merely flip a flag: it takes OVER from what the
// browser reports, so anything it lets through displaces real measurements everywhere. The four
// insets are the only fields the web layer acts on today, so they are validated as numbers and the
// rest is normalised to its "unknown" value rather than trusted.
function readSafeAreaPayload(raw: unknown): SafeAreaInsets | null {
  const s = raw as Partial<SafeAreaInsets> | null | undefined;
  if (s === null || typeof s !== 'object') return null;
  const sides = [s.left, s.top, s.right, s.bottom];
  if (sides.some((n) => typeof n !== 'number' || !Number.isFinite(n) || n < 0)) return null;
  return { left: s.left as number, top: s.top as number, right: s.right as number, bottom: s.bottom as number };
}

// 20260919 - IPHONE DUO SIDE DOCK: the division guard, and the reason it exists BEFORE anything
// reads a division. Nothing in the web layer consumes the fold rectangle today — the harness builds
// its own well-formed one (components/debugViewports.ts) and the only other writer is this verb —
// so an unvalidated pass-through here fails silently and would surface later as a crash rather than
// a wrong layout: the first consumer of this field will reach straight for `division.rect.x` to keep
// a panel off the hinge, and `undefined.x` is a blank WebView. The four insets are guarded for the
// opposite reason (they displace real measurements immediately); this one is guarded because the
// cost of a bad value is deferred, which makes it easier to ship and harder to trace.
//
// Anything malformed resolves to `null` — "no fold I can vouch for" — which is the same answer every
// non-foldable host already gives, and a shape the consumers have to handle regardless.
function readDivisionPayload(raw: unknown): FoldDivision | null {
  const d = raw as Partial<FoldDivision> | null | undefined;
  if (d === null || d === undefined || typeof d !== 'object') return null;
  if (typeof d.active !== 'boolean') return null;
  if (d.axis !== 'vertical' && d.axis !== 'horizontal') return null;

  const r = d.rect as Partial<FoldDivision['rect']> | null | undefined;
  if (r === null || r === undefined || typeof r !== 'object') return null;
  // x/y may be 0 but never negative in viewport coordinates; a zero-area band is not a region.
  if ([r.x, r.y].some((n) => typeof n !== 'number' || !Number.isFinite(n) || n < 0)) return null;
  if ([r.width, r.height].some((n) => typeof n !== 'number' || !Number.isFinite(n) || n <= 0)) return null;

  return {
    active: d.active,
    axis: d.axis,
    rect: { x: r.x as number, y: r.y as number, width: r.width as number, height: r.height as number },
  };
}

// 20260928 - THE LOCALE LOAD IN FLIGHT, exported so a view can wait for it. A non-English locale is
// a lazy chunk (i18n.ts loadLanguage), so for one round trip after `setLocale` the page still
// renders English. The canvas overlay (bridge/features/canvasOverlay.ts showView) mounts its view
// in a WebView that is about to be revealed, and would otherwise reveal an English form that swaps
// language a frame later; it awaits this instead. Behaviour of setLocale itself is unchanged: the
// same chain runs, this only keeps a handle on it while it is pending.
let localeLoadInFlight: Promise<void> | null = null;

/** The `setLocale` load still in flight, or null when none is. Settles when the language has been
 *  applied (or its load failed — callers that wait on it must handle both). */
export function getLocaleLoadInFlight(): Promise<void> | null {
  return localeLoadInFlight;
}

export const settingsHandlers = {
  setUIScaling: (msg: SetUIScalingMessage) => {
    import('@/store/uiStore.ts').then(({ useUiStore }) => {
      useUiStore.getState().setUIScaleLevel(msg.payload.level);
    });
  },
  setLocale: (msg: SetLocaleMessage) => {
    const load = import('@/i18n').then(async ({ loadLanguage }) => {
      const { default: i18next } = await import('i18next');
      await loadLanguage(msg.payload.locale);
      i18next.changeLanguage(msg.payload.locale);
    });
    localeLoadInFlight = load;
    // finally(), not then(settle, settle): a failed load still surfaces as the ONE unhandled
    // rejection it always did, rather than being quietly swallowed by this bookkeeping. Only the
    // LATEST load clears the handle, so an earlier one settling late cannot hide a later one.
    void load.finally(() => { if (localeLoadInFlight === load) localeLoadInFlight = null; });
  },
  setFeatureFlags: (msg: SetFeatureFlagsMessage) => {
    if (typeof msg.payload?.scriptCoverage === 'boolean') {
      useUiStore.getState().setScratchScriptCoverage(msg.payload.scriptCoverage);
    }
    if (typeof msg.payload?.screenplayBlocking === 'boolean') {
      useUiStore.getState().setScreenplayBlocking(msg.payload.screenplayBlocking);
    }
    if (typeof msg.payload?.sceneShotSettings === 'boolean') {
      useUiStore.getState().setSceneShotSettings(msg.payload.sceneShotSettings);
    }
  },
  setInputCapabilities: (msg: SetInputCapabilitiesMessage) => {
    // Typed guard, like setFeatureFlags above: a malformed push must leave the seeded URL answer
    // alone rather than resolve to "no keyboard" and silently switch auto-focus off.
    //
    // 20260923 - PER-KEY, exactly as setFeatureFlags is: each device is applied only if this payload
    // actually named it. An absent key is not `false` — it is a push about the OTHER device, and
    // flattening it would let a keyboard attach claim the trackpad had been unplugged.
    if (typeof msg.payload?.hardwareKeyboard === 'boolean') {
      useInputCapabilitiesStore.getState().setHardwareKeyboardAttached(msg.payload.hardwareKeyboard);
    }
    if (typeof msg.payload?.pointerDevice === 'boolean') {
      useInputCapabilitiesStore.getState().setPointerDeviceAttached(msg.payload.pointerDevice);
    }
  },
  setFoldState: (msg: SetFoldStateMessage) => {
    const p = msg.payload as Partial<SetFoldStateMessage['payload']> | null | undefined;
    if (p === null || typeof p !== 'object') return;

    const safeArea = readSafeAreaPayload(p.safeArea);
    if (safeArea === null) return;
    if (typeof p.foldable !== 'boolean') return;

    // 20260926 - THE OCCLUSION PAIR AND ITS EXTENTS, read once here so the two are judged
    // together. The extents are believed only beside a pair that is itself a reading, and only
    // both at once, each a finite 0..400 (platform/foldState.ts readOcclusionExtent). Anything else
    // drops THE EXTENTS and keeps the pair — the dock then spends the 96px allowance at an occluded
    // end, which is exactly what it did before the extents existed. Never the other way round: a bad
    // extent must not cost the pair, and a pair that is not a reading has no extents to describe.
    const barHeadOccluded = typeof p.barHeadOccluded === 'boolean' ? p.barHeadOccluded : null;
    const barFootOccluded = typeof p.barFootOccluded === 'boolean' ? p.barFootOccluded : null;
    const headExtent = readOcclusionExtent(p.barHeadExtent);
    const footExtent = readOcclusionExtent(p.barFootExtent);
    const extentsKept = barHeadOccluded !== null && barFootOccluded !== null
      && headExtent !== null && footExtent !== null;

    // 20260920 - AND THE MARK GOES HERE: after the guards, BEFORE the write. This push is the only
    // signal for a fold or an unfold that the page itself cannot see, and it rewrites `--safe-*`,
    // which moves every shell's padding and re-wraps the board's grid — a layout change that
    // reaches the grid through a ResizeObserver a task later, with no `resize` of its own to blame
    // it on. Everything that moves because of it must SNAP, so the flag has to be up before the
    // store write that starts it, in the same task.
    //
    // AFTER the guards because a rejected push changes nothing and must not suppress a user's
    // animation; BEFORE the write because setBridgeFoldState notifies its subscribers
    // synchronously — `publishSafeAreaProperties` runs inside it, and a React commit can follow in
    // the same task (the bridge dispatch is a discrete event).
    //
    // UNCONDITIONAL, and that cannot latch the flag: the native sender de-dupes. Unity's
    // PushFoldStateToWebView compares each per-frame reading against the last one it sent and
    // returns early when they are equal (TimelineWebViewPanel.cs), so this verb arrives on a real
    // pose CHANGE (plus once per page load) rather than every frame. Re-deriving "did this change
    // the resolved answer?" here would be a second, drifting copy of that same question.
    markViewportChange();

    useFoldStateStore.getState().setBridgeFoldState({
      foldable: p.foldable,
      display: p.display === 'inner' || p.display === 'outer' ? p.display : 'unknown',
      posture: p.posture === 'closed' || p.posture === 'partiallyOpen' || p.posture === 'fullyOpen'
        ? p.posture
        : 'unknown',
      division: readDivisionPayload(p.division),
      // 20260920 - THE MEASURED INSETS OUTRANK THE PUSHED TRAIT WHENEVER THEY NAME A SIDE, and
      // this used to be the other way round: the trait was taken whenever it was present and the
      // derivation was only its fallback. The owner turned a Duo 180° in the closed landscape pose
      // and photographed what that costs — the page inset itself on the LEFT while the dock stayed
      // on the RIGHT, over the panel, because the last trait pushed still said `trailing`.
      //
      // The two fields answer different questions and only one of them is the dock's: the trait is
      // the system's PREFERRED edge "regardless of whether a vertical bar is currently visible"
      // (UIVerticalBarEdge.h, 27.1 SDK), while the insets are where space has actually been
      // RESERVED — and this column is pinned flush to a window edge, so it has to be on the side
      // that has the space or it lands on the page. platform/foldState.ts resolveVerticalBarEdge()
      // holds that argument in full; it is shared with the store's URL-seed branch so the two
      // transports cannot answer the same pose differently.
      //
      // The trait still decides where the insets cannot — the all-zeros pane it exists for, where
      // the derivation is null by design — and an absent or unrecognised value still falls through
      // to the insets rather than to a bare null, which is the one "unknown" the store wants.
      verticalBarEdge: resolveVerticalBarEdge(p.verticalBarEdge, safeArea),
      // 20260919 - THE GATE FIELD. resolveVerticalBarWidth() will only synthesize a rail for an edge
      // whose inset is zero when this is true, because the 27.1 `verticalBarEdge` trait names the
      // system's PREFERRED edge "regardless of whether a vertical bar is currently visible". Reading
      // it here is what makes the pushed source able to answer the question at all: this source
      // OUTRANKS the URL seed and never retires, so a push that omits the flag does not merely fail
      // to dock — it UNDOCKS a pane that the seed had already got right.
      // `=== true` rather than a truthiness test, for the same reason every other field on this verb
      // is validated: a malformed push must resolve to the conservative answer, not to a confident one.
      splitView: p.splitView === true,
      // 20260920 - READ AS `boolean | null`, AND NOT WITH `=== true`. Every other field on this verb
      // collapses a malformed value to the conservative answer, and for those the conservative answer
      // IS false. Not here: these two say which END of the docked column owes a camera/status
      // clearance, `false` means MEASURED CLEAR (spend nothing there), and a push that simply predates
      // the keys has measured nothing at all. Flattening absent to false would take 96px of clearance
      // off an end on the strength of a message that never mentioned it — the one failure direction
      // that puts a control under a camera rather than merely wasting space. `null` keeps the third
      // value, and platform/foldState.ts resolveHousingEnds() is where it becomes a geometry again.
      barHeadOccluded,
      barFootOccluded,
      barHeadExtent: extentsKept ? headExtent : null,
      barFootExtent: extentsKept ? footExtent : null,
      // 20260929 - THE LAPTOP BAND (window fractions, top-left origin; platform/foldState.ts
      // FoldState.laptopBand). Guarded like the canvas overlay's region: anything malformed — and the
      // explicit `null` Unity sends outside the laptop pose — is "no band", which is the answer every
      // other host gives. A bad band never costs the rest of an otherwise sound push.
      laptopBand: readLaptopBand(p.laptopBand),
      safeArea,
    });
  },
  setDeviceId: (msg: SetDeviceIdMessage) => {
    // Entitlement BEFORE deviceId: setDeviceId migrates the pre-handshake buffer into the
    // queue and kicks a flush, and flushNow backfills from the store, so the store must
    // already hold the value when that happens.
    if (isTelemetryEntitlement(msg.payload.entitlement)) {
      useTelemetryStore.getState().setEntitlement(msg.payload.entitlement);
    }
    useTelemetryStore.getState().setDeviceId(msg.payload.deviceId);
    // Buffered events that migrated to the queue need an explicit flush kick;
    // setDeviceId doesn't schedule one on its own, and single-event flows
    // (welcome view mount, signin view mount) would otherwise sit forever.
    scheduleFlush();
  },
  setFunnelId: (msg: SetFunnelIdMessage) => {
    useTelemetryStore.getState().setFunnelId(msg.payload.funnelId);
  },
  closePanel: (_msg: ClosePanelMessage) => {
    // Unity's OnCloseButton fallback (TimelineWebViewPanel.cs:8705) sends this when
    // window.__requestBack is absent. Historically unhandled; explicit no-op keeps
    // the unclaimed counter at 0 and documents the intent.
    diagLog('Bridge', 'closePanel received (legacy Unity fallback; no-op)');
  },
} satisfies HandlerMap;
